/*
 * FileName : index.ts
 * ProjectName : admin
 * Author : terrorblade
 * Created Date: 2021-07-28 16:35:59
 * Description :
 * -----
 * Last Modified:
 * Modified By :
 * -----
 * Copyright (c) 2021 Magina Corporation. All rights reserved.
 */

import { get, post } from 'src/api/http';
import * as types from './type.d';
import { SearchSelectParams, SearchSelectRows, ReturnPageData } from 'src/api/types';
import { DcuAlarmEvent } from 'src/api/collectReport/dcuAlarmEvent/types';

class Common {
    // 获取系统版本号相关信息
    getSystemVersion (): Promise<{ data: types.SystemVersion[] }> {
        let url = '/business/common/mdr-home-page/system-version';

        if (window.config.project === 'PVMS') {
            url = '/business/common/prepaid-home-page/system-version';
        }
        return get(url);
    }
    getTodayAlarmData (type: string, page:number = 1): Promise<types.GetAlarmList[]> {
        return get(`/report/rptamr/event/today-alarm-data?alarmType=${type}&page=${page}`);
    }
    getAlarmDataSum (type: string): Promise<number[]> {
        return get(`/report/rptamr/event/alarm-data-sum?alarmType=${type}`);
    }
    updateAlarmStatus (data: types.updateAlarm) {
        return post('/report/rptamr/event/update-alarm-status', data);
    }
    // 获取设备树相关数据
    getDeviceTree (data: types.DeviceTreeParams): Promise<types.DeviceTreeData> {
        return get('/business/common/common-box/init-device-tree', data);
    }
    // 设备树获取子节点数据
    getDeviceChildNode (data: types.DeviceChildParams): Promise<types.DeviceChildData[]> {
        return get('/business/common/common-box/device-tree-child-node', data);
    }
    // 表计schedule数据
    getMeterScheduleInfo (data:types.MeterInfoParams): Promise<types.MeterScheduleInfoData[]> {
        return get(`/amr/device-info/meter-schedule?meterNo=${data.METERNO}&meterAddress=${data.METERADDRESS}&meterGuid=${data.METERGUID}`);
    }
    // 表计基础数据
    getMeterBaseInfo (data: types.MeterInfoParams): Promise<types.MeterBaseInfoData[]> {
        return get(`/amr/device-info/meter-base?meterNo=${data.METERNO}&meterAddress=${data.METERADDRESS}&meterGuid=${data.METERGUID}`);
    }
    // 表计ami数据
    getMeterAMIInfo (data:types.MeterInfoParams): Promise<types.MeterAMIInfoData> {
        return get(`/amr/device-info/meter-ami?meterNo=${data.METERNO}&meterAddress=${data.METERADDRESS}&meterGuid=${data.METERGUID}`);
    }
    // 获取集中器AMI信息
    getDcuAmi (data: types.DCUAmiParams):Promise<types.DCUAmiData> {
        return get('/amr/device-info/dcu-ami-new', data);
    }
    // 获取集中器baseData数据
    getDcuBaseData (data: types.DCUAmiParams): Promise<types.DCUBaseData[]> {
        return get('/amr/device-info/dcu-base', data);
    }
    // 获取集中器log信息
    getDcuLog (data: types.DCUAmiParams):Promise<types.DCULogData[]> {
        return get('/amr/device-info/dcu-log', data);
    }
    // 获取集中器schedule信息
    getDcuSchedule (data: types.DCUAmiParams): Promise<types.MeterScheduleInfoData[]> {
        return get('/amr/device-info/dcu-schedule', data);
    }
    getMeterNodeByDcuId (data:types.MeterNodeByDcuIdParams): Promise<types.DeviceChildData[]|null> {
        return get('/business/common/common-box/meter-nod-eby-dcuid', data);
    }
    getNodeByLoginUser (subSysNo:string): Promise<types.NodeByLoginUserData[]> {
        return get('/business/common/iframe/node-login-user?subSysNo=' + subSysNo);
    }

    getMeterNodesByDcuId (data:types.MeterNodeByDcuIdParams): Promise<types.DeviceChildData[]|null> {
        return get('/amr/loadingfiles/getMeterNodeByDcuId', data);
    }
    getMeterNoBatchList (data:types.MeterNoBatchParams):Promise<types.MeterNoBatchRows[]> {
        return get('/business/common/iframe/getMeterNoBatchList', data);
    }
    getInstoreMeterNoList (data: types.InstoreMeterListParams): Promise<types.InstoreMeterListRows[]> {
        return get('/business/common/iframe/getInstoreMeterNoList', data);
    }
    getDstList (data: types.GetDstListParasm): Promise<types.GetDstListData[]> {
        return get('/business/archive/dst/getDstList', data);
    }
    getMeterRegisterType (subSysNo:string, baseType?:string):Promise<types.MeterRegisterTypeRows[]> {
        return get('/business/common/iframe/getMeterRegisterType', {subSysNo, baseType});
    }
    getMeterSignalStrength (cstId:string):Promise<types.MeterSignalStrengthRows[]> {
        return get('/amr/network-management/meter-signal-strength', {cstId});
    }
    getDcuNo (data: SearchSelectParams): Promise<ReturnPageData<SearchSelectRows>> {
        return get('/business/archive/cst/getDcuNo', data);
    }
    getDcu (data: SearchSelectParams, query:types.QuerySearchSelectDCUParmas): Promise<ReturnPageData<SearchSelectRows>> {
        return get('/business/archive/cst/getDcu', {...data, ...query});
    }
    getMeterNo (data: SearchSelectParams): Promise<ReturnPageData<SearchSelectRows>> {
        return get('/amr/meter/getMeterNo', data);
    }
    getCustomerNo (data: SearchSelectParams): Promise<ReturnPageData<SearchSelectRows>> {
        return get('/amr/customer/getCustomerNo', data);
    }
    getLastTenEvent (data: types.QueryLastTenEventParam): Promise<DcuAlarmEvent[]> {
        return post('/report/rptamr/event/lastTenEvent', data);
    }
    initDeviceTreeByGis (data: types.QueryInitDeviceTreeParams): Promise<types.InitDeviceTreeData> {
        return get('/device-tree/init-gis-device-tree', data);
    }
    getChildrenNodeByGis (data:types.InitChildParams): Promise<types.InitChildRows[]> {
        return get('/device-tree/child-node', data);
    }
    getGroupDetailListByCodeAndNo (data: SearchSelectParams, query?: types.QuerySearchSelectGroupParmas): Promise<ReturnPageData<SearchSelectRows>> {
        return get('business/system/classification/getGroupDetailListByCodeAndNo', {...data, ...query});
    }
    getNoForMeterType (meterNo: string): Promise<types.MeterTypeRows> {
        return get('business/common/mdr-home-page/getMeterCstTypeByMeterNo', {meterNo});
    }
    getCstTypeByNo (dcuNo: string): Promise<types.DCUTypeRows> {
        return get('/business/archive/cst/getCstTypeByNo', {cstNo: dcuNo});
    }
    getNoForCstType (cstNo: string): Promise<types.CstTypeRows> {
        return get('business/common/mdr-home-page/getCstTypeByCstNo', {cstNo});
    }
    getOutageDcuNodeInfo ():Promise<types.InitDeviceTreeRows[]> {
        return get('/business/common/mdr-home-page/getOutageDcuNodeInfo');
    }
    getGeoFile (): Promise<string[]> {
        return get('/device-tree/get-geo-file');
    }
    initDcuByGis (cstTypes: string[]): Promise<types.InitDeviceTreeData> {
        return get('/device-tree/init-gis-dcu', {cstType: cstTypes.join(',')});
    }
    getMeterChangedata (data: types.QueryMeterChangeDataParams): Promise<types.MeterChangeData> {
        return get('/device-tree/gis-change-status', data);
    }
    getMeterTypeList (): Promise<types.MeterTypeListRow[]> {
        return get('/business/common/mdr-home-page/getMeterTypeList');
    }
    getCaptcha (): Promise<string> {
        return get('captcha');
    }
}

export default new Common();
